import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import AnalyticsService from "../../../services/analytics/AnalyticsService";
import { BRANDING_THEME as BRAND_COLORS } from "../../../constants/brandingTheme";
import { MONTH_SHORT, fmtInt, fmtMoney, pctChange, totalCreated } from "../usagePeriods";
import { aiEstimatedCostUsd } from "../pricing";
import logger from "../../../utils/logger";

// Paleta de series validada para el fondo de las cards (dark): verde/azul/rojo separados
// para visión normal y daltonismo. No usar primary.main (#25D366) en líneas: es demasiado
// claro sobre este fondo para distinguirse del amarillo/blanco.
const SERIES = { green: '#1aa554', blue: '#4285F4', red: '#ef4444' };

const tooltipStyle = {
  backgroundColor: BRAND_COLORS.backgrounds.primary,
  border: `1px solid ${BRAND_COLORS.borders.light}`,
  color: BRAND_COLORS.text.primary,
  fontSize: '0.8rem',
};

function Delta({ current, previous, money }) {
  const pct = pctChange(current, previous);
  const fmt = money ? fmtMoney : fmtInt;
  let color = BRAND_COLORS.text.muted;
  let text = 'sin base';
  if (pct !== null) {
    text = `${pct > 0 ? '↑' : pct < 0 ? '↓' : '='} ${Math.abs(pct)}%`;
    color = pct > 0 ? BRAND_COLORS.primary.main : pct < 0 ? BRAND_COLORS.error : BRAND_COLORS.text.muted;
  }
  return (
    <span style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.78rem' }}>
      {fmt(previous)} → <span style={{ color, fontWeight: 600 }}>{text}</span>
    </span>
  );
}

function ChartCard({ title, data, lines }) {
  const empty = !data.length;
  return (
    <div className="sa-chart" style={{ backgroundColor: BRAND_COLORS.backgrounds.primary, borderColor: BRAND_COLORS.borders.light }}>
      <h6 style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{title}</h6>
      {empty ? (
        <div style={{ color: BRAND_COLORS.text.muted, fontSize: '0.8rem', padding: '2rem 0', textAlign: 'center' }}>Sin actividad en el periodo</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke={BRAND_COLORS.borders.light} vertical={false} />
            <XAxis dataKey="day" stroke={BRAND_COLORS.text.muted} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis stroke={BRAND_COLORS.text.muted} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} labelFormatter={(d) => `Día ${d}`} />
            <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
            {lines.map((l) => (
              <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/**
 * Detalle de uso de un cliente: serie diaria del periodo, comparativa contra el periodo
 * anterior y los 12 meses del año. `current`/`previous` son las filas ya sumadas del
 * overview (evita pedir de nuevo lo que la tabla ya tiene).
 */
export default function ClientUsageDetail({ clientId, period, current, previous, compact = false }) {
  const [series, setSeries] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      AnalyticsService.getClientSeries(clientId, period.from, period.to),
      AnalyticsService.getClientMonthly(clientId, year),
    ])
      .then(([s, m]) => {
        if (!alive) return;
        setSeries((s?.series || []).map((d) => ({
          day: d.date.slice(5), // 'MM-DD'
          recibidos: d.messagesReceived,
          enviados: d.messagesSent,
          creadas: totalCreated(d),
          confirmadas: d.appointmentsConfirmed,
          canceladas: d.appointmentsCancelled,
        })));
        setMonths(m?.months || []);
      })
      .catch((e) => logger.error('Error cargando detalle de uso:', e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [clientId, period.from, period.to, year]);

  const c = current || {};
  const p = previous || {};
  const rows = [
    { label: 'Mensajes recibidos', cur: c.messagesReceived, prev: p.messagesReceived },
    { label: 'Mensajes enviados', cur: c.messagesSent, prev: p.messagesSent },
    { label: 'Citas creadas', cur: totalCreated(c), prev: totalCreated(p) },
    { label: 'Citas confirmadas', cur: c.appointmentsConfirmed, prev: p.appointmentsConfirmed },
    { label: 'Citas canceladas', cur: c.appointmentsCancelled, prev: p.appointmentsCancelled },
    { label: 'Recordatorios', cur: c.remindersSent, prev: p.remindersSent },
    { label: 'Valor agendado', cur: (c.revenueBooked || 0) - (c.revenueCancelled || 0), prev: (p.revenueBooked || 0) - (p.revenueCancelled || 0), money: true },
  ];

  const src = c.appointmentsCreated || {};
  const created = totalCreated(c);

  return (
    <div className={`sa-usage-detail ${compact ? "is-compact" : ""}`}>
      <div className="sa-usage-grid">
        <div>
          <h5 className="h6 mb-2" style={{ color: BRAND_COLORS.primary.main, fontSize: '0.9rem' }}>vs {period.prevLabel}</h5>
          <table className="sa-compare">
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <td style={{ color: BRAND_COLORS.text.secondary }}>{r.label}</td>
                  <td style={{ color: BRAND_COLORS.text.primary, fontWeight: 700 }}>{r.money ? fmtMoney(r.cur) : fmtInt(r.cur)}</td>
                  <td><Delta current={r.cur} previous={r.prev} money={r.money} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2" style={{ color: BRAND_COLORS.text.muted, fontSize: '0.75rem' }}>
            Origen de las citas: bot {fmtInt(src.bot)} · web {fmtInt(src.web)} · panel {fmtInt(src.panel)}
            {created > 0 && src.panel === created && (
              <span style={{ color: BRAND_COLORS.warning }}> — todo se captura a mano, no usan la automatización</span>
            )}
          </div>
          {/* Asistente con IA: solo aparece si el negocio lo usó en el periodo. Costo con precio de lista del modelo nano. */}
          {(c.aiConversations > 0 || c.aiTokensIn > 0) && (
            <div className="mt-1" style={{ color: BRAND_COLORS.text.muted, fontSize: '0.75rem' }}>
              Asistente IA: {fmtInt(c.aiConversations)} conversaciones · {fmtInt((c.aiTokensIn || 0) + (c.aiTokensOut || 0))} tokens · ≈ USD {aiEstimatedCostUsd(c.aiTokensIn, c.aiTokensOut).toFixed(2)}
              {c.aiFallbacks > 0 && <span style={{ color: BRAND_COLORS.warning }}> · {fmtInt(c.aiFallbacks)} veces cayó al bot de reglas</span>}
            </div>
          )}
          {(c.messagesErrors > 0 || c.whatsappSessionDrops > 0 || c.remindersFailed > 0) && (
            <div className="mt-1" style={{ color: BRAND_COLORS.warning, fontSize: '0.75rem' }}>
              Salud: {fmtInt(c.messagesErrors)} errores del bot · {fmtInt(c.whatsappSessionDrops)} caídas de WhatsApp · {fmtInt(c.remindersFailed)} recordatorios fallidos
            </div>
          )}
        </div>
        <div className="sa-charts">
          {loading ? (
            <div style={{ color: BRAND_COLORS.text.muted, fontSize: '0.8rem' }}>Cargando…</div>
          ) : (
            <>
              <ChartCard title="Mensajes por día" data={series} lines={[
                { key: 'recibidos', name: 'Recibidos', color: SERIES.blue },
                { key: 'enviados', name: 'Enviados', color: SERIES.green },
              ]} />
              <ChartCard title="Citas por día" data={series} lines={[
                { key: 'creadas', name: 'Creadas', color: SERIES.blue },
                { key: 'confirmadas', name: 'Confirmadas', color: SERIES.green },
                { key: 'canceladas', name: 'Canceladas', color: SERIES.red },
              ]} />
            </>
          )}
        </div>
      </div>

      {months.length > 0 && (
        <div className="mt-3">
          <h5 className="h6 mb-2" style={{ color: BRAND_COLORS.primary.main, fontSize: '0.9rem' }}>{year} mes a mes</h5>
          <div className="sa-months-scroll">
            <table className="sa-months">
              <thead>
                <tr>
                  <th style={{ color: BRAND_COLORS.text.muted }}></th>
                  {MONTH_SHORT.map((m) => <th key={m} style={{ color: BRAND_COLORS.text.muted }}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Mensajes', get: (x) => fmtInt((x.messagesReceived || 0) + (x.messagesSent || 0)) },
                  { label: 'Citas', get: (x) => fmtInt(totalCreated(x)) },
                  { label: 'Valor', get: (x) => fmtMoney((x.revenueBooked || 0) - (x.revenueCancelled || 0)) },
                ].map((r) => (
                  <tr key={r.label}>
                    <td style={{ color: BRAND_COLORS.text.secondary }}>{r.label}</td>
                    {months.map((mo, i) => (
                      <td key={i} style={{ color: BRAND_COLORS.text.primary, opacity: i > new Date().getMonth() ? 0.35 : 1 }}>{r.get(mo)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
